/**
 * Geheimhouding (23 okt 2026; op main in Admin → Klantvereisten, op develop onder Leveranciers): getekende geheimhoudingsverklaringen van freelancers, voor alle
 * klanten of voor één klantgroep (bv. Schneider Electric: tekenen vóór de briefing).
 * Opslag in Toolframe; een actieve Airtable-overeenkomst met een NDA-template staat er als
 * "uit Airtable" bij. Het project, de draaidag en de callsheet waarschuwen als het ontbreekt.
 *
 * Vangnetten: vastleggen = gewoon opslaan; verwijderen = bevestigen.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { bevestig } from '../../../utils/bevestig';
import { leesJson, meldFout } from '../../../utils/apiFout';
import { wisKlantVereisten } from '../../../utils/klantVereisten';

// main: de Leveranciers-pagina (met hrUi) bestaat hier nog niet — zelfde kleuren en helper inline.
const INK = { tekst: '#2d3335', zacht: '#556071', stil: '#8a94a3' };
const haalJson = async (url, wat, opties) => leesJson(await authFetch(url, opties), wat);

const API = `${BACKEND_URL}/Api/klantvereisten/geheimhouding`;
const vandaag = () => new Date().toLocaleDateString('sv-SE');
const datumNl = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function Vastleggen({ open, onClose, data, voorgekozen, onKlaar }) {
  const [v, setV] = useState({});
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  useEffect(() => {
    if (open) {
      setFout(null);
      setV({ persoon: voorgekozen || null, getekendOp: vandaag(), documentUrl: '', klantgroepId: '', opmerking: '' });
    }
  }, [open, voorgekozen]);
  const zet = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));

  const opslaan = async () => {
    setFout(null);
    setBezig(true);
    try {
      await haalJson(API, 'Vastleggen', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          naam: v.persoon?.naam, email: v.persoon?.email, operationeelId: v.persoon?.id,
          getekendOp: v.getekendOp, documentUrl: v.documentUrl, klantgroepId: v.klantgroepId || null, opmerking: v.opmerking,
        }),
      });
      toast.success('Geheimhoudingsverklaring vastgelegd');
      wisKlantVereisten();
      onKlaar();
    } catch (e) {
      setFout(e.message);
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Geheimhoudingsverklaring vastleggen</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {fout && <Alert severity="error">{fout}</Alert>}
        <Autocomplete options={data?.freelancers || []} value={v.persoon || null} getOptionLabel={(o) => o.naam || ''}
          isOptionEqualToValue={(a, b) => a.id === b.id} onChange={(_e, p) => setV((x) => ({ ...x, persoon: p }))}
          renderInput={(p) => <TextField {...p} label="Freelancer" required />} />
        <TextField type="date" label="Getekend op" value={v.getekendOp || ''} onChange={zet('getekendOp')}
          InputLabelProps={{ shrink: true }} inputProps={{ max: vandaag() }} required />
        <TextField label="Link naar het getekende document" value={v.documentUrl || ''} onChange={zet('documentUrl')}
          placeholder="https://… (SharePoint, Airtable-bijlage)" />
        <TextField select label="Geldt voor" value={v.klantgroepId ?? ''} onChange={zet('klantgroepId')}>
          <MenuItem value="">Alle klanten</MenuItem>
          {(data?.klantgroepen || []).map((g) => <MenuItem key={g.id} value={g.id}>Alleen {g.titel}</MenuItem>)}
        </TextField>
        <TextField label="Opmerking" value={v.opmerking || ''} onChange={zet('opmerking')} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button variant="contained" disableElevation onClick={opslaan} disabled={bezig || !v.persoon || !v.getekendOp}>
          {bezig ? 'Bezig…' : 'Vastleggen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Geheimhouding({ magBewerken }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [dialoog, setDialoog] = useState(null);   // { persoon } | null
  const [alleenZonder, setAlleenZonder] = useState(false);
  const [zoek, setZoek] = useState('');

  const laad = useCallback(async () => {
    try { setData(await haalJson(API, 'Geheimhouding laden')); setFout(null); }
    catch (e) { setFout(e.message); setData({ verklaringen: [], freelancers: [], klantgroepen: [], uitAirtable: [] }); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const groepNaam = useMemo(() => Object.fromEntries((data?.klantgroepen || []).map((g) => [g.id, g.titel])), [data]);

  const rijen = useMemo(() => {
    if (!data) return [];
    const perId = {};
    const perNaam = {};
    for (const v of data.verklaringen) {
      // Met een persoons-id hoort de verklaring alleen bij die persoon (geen naamgenoot, Codex 23 okt).
      if (v.operationeelId) (perId[v.operationeelId] ||= []).push(v);
      else (perNaam[(v.naam || '').toLowerCase()] ||= []).push(v);
    }
    const airtable = new Set((data.uitAirtable || []).map((a) => (a.naam || '').toLowerCase()));
    const z = zoek.trim().toLowerCase();
    return data.freelancers
      .map((p) => {
        const lijst = [...(perId[p.id] || []), ...(perNaam[(p.naam || '').toLowerCase()] || [])];
        return { ...p, verklaringen: lijst, airtable: airtable.has((p.naam || '').toLowerCase()) };
      })
      .filter((r) => (!alleenZonder || (!r.verklaringen.length && !r.airtable)) && (!z || r.naam.toLowerCase().includes(z)));
  }, [data, alleenZonder, zoek]);

  const verwijder = async (v) => {
    const ok = await bevestig({
      titel: 'Verklaring verwijderen?',
      tekst: `De verklaring van ${v.naam} (getekend ${datumNl(v.getekendOp)}) verdwijnt. Op projecten met een NDA-plicht staat ${v.naam} daarna weer als "ontbreekt".`,
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      await haalJson(`${API}/${v.id}`, 'Verwijderen', { method: 'DELETE' });
      wisKlantVereisten();
      laad();
    } catch (e) { meldFout(e, 'Verwijderen'); }
  };

  if (!data) return <CircularProgress size={22} />;
  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: INK.zacht, mb: 2, maxWidth: 820 }}>
        Klanten zoals Schneider Electric eisen dat freelancers vóór de briefing een geheimhoudingsverklaring tekenen.
        Leg hier vast wie getekend heeft; het project, de draaidag en de callsheet waarschuwen als het ontbreekt.
      </Typography>
      {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}
      {data.airtableStatus === 'niet_beschikbaar' && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          De overeenkomsten uit Airtable konden nu niet gelezen worden: wie daar een geheimhoudingsverklaring heeft, staat hieronder mogelijk als "Nog niet".
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" placeholder="Zoek freelancer" value={zoek} onChange={(e) => setZoek(e.target.value)} />
        <FormControlLabel control={<Switch size="small" checked={alleenZonder} onChange={(e) => setAlleenZonder(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 14 }}>Alleen zonder verklaring</Typography>} />
        <Box sx={{ flex: 1 }} />
        {magBewerken && <Button onClick={() => setDialoog({ persoon: null })} sx={{ textTransform: 'none' }}>+ Verklaring vastleggen</Button>}
      </Box>
      {data.freelancers.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: INK.stil }}>Geen actieve freelancers in Personen gevonden.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Freelancer</TableCell><TableCell>Getekend</TableCell><TableCell>Geldt voor</TableCell>
              <TableCell>Document</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rijen.map((r) => (r.verklaringen.length ? r.verklaringen : [null]).map((v, i) => (
              <TableRow key={`${r.id}-${v?.id || 'leeg'}`}>
                <TableCell sx={{ color: INK.tekst }}>{i === 0 ? r.naam : ''}</TableCell>
                <TableCell sx={{ color: v ? INK.tekst : INK.stil }}>
                  {v ? datumNl(v.getekendOp) : (r.airtable ? 'Overeenkomst in Airtable' : 'Nog niet')}
                </TableCell>
                <TableCell sx={{ color: INK.zacht }}>{v ? (v.klantgroepId ? groepNaam[v.klantgroepId] || 'Klantgroep' : 'Alle klanten') : ''}</TableCell>
                <TableCell>
                  {v?.documentUrl ? <a href={v.documentUrl} target="_blank" rel="noopener noreferrer">Openen</a> : ''}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {magBewerken && !v && <Button size="small" onClick={() => setDialoog({ persoon: r })} sx={{ textTransform: 'none' }}>Vastleggen</Button>}
                  {magBewerken && v && (
                    <Tooltip title="Verwijderen">
                      <IconButton size="small" onClick={() => verwijder(v)} aria-label={`Verklaring ${r.naam} verwijderen`}>
                        <DeleteOutlineIcon sx={{ fontSize: 18, color: INK.stil }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
      )}
      <Vastleggen open={!!dialoog} voorgekozen={dialoog?.persoon} data={data} onClose={() => setDialoog(null)}
        onKlaar={() => { setDialoog(null); laad(); }} />
    </Box>
  );
}
